import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_EVENT_REDIRECT_LINK } from "./graphql/queries";
import { Box, CircularProgress, Typography, Link } from "@mui/material";
import { Helmet } from "react-helmet-async";

/**
 * Component for redirecting users to registration links
 * with proper meta tags for social media sharing
 */
const EventRedirect = () => {
  const { eventId, role } = useParams();
  const [countdown, setCountdown] = useState(5);

  // Fetch redirect link data
  const { loading, error, data } = useQuery(GET_EVENT_REDIRECT_LINK, {
    variables: { eventId, role },
    fetchPolicy: "network-only",
  });

  //   useEffect(() => {
  //     let redirectTimer;
  //     let countdownTimer;

  //     if (data?.getEventRedirectLink) {
  //       const { link, utmLink } = data.getEventRedirectLink;

  //       // Create redirect URL
  //       const baseUrl = `https://eventegration.com${link}`;
  //       const fullUrl = utmLink ? `${baseUrl}?${utmLink}` : baseUrl;

  //       // Set up countdown timer
  //       countdownTimer = setInterval(() => {
  //         setCountdown((prev) => {
  //           if (prev <= 1) {
  //             clearInterval(countdownTimer);
  //             return 0;
  //           }
  //           return prev - 1;
  //         });
  //       }, 1000);

  //       // Set up redirect
  //       redirectTimer = setTimeout(() => {
  //         window.location.replace(fullUrl);
  //       }, 5000); // 5 seconds
  //     }

  //     return () => {
  //       clearTimeout(redirectTimer);
  //       clearInterval(countdownTimer);
  //     };
  //   }, [data]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <CircularProgress color="primary" size={50} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          p: 3,
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Error Loading Registration Link
        </Typography>
        <Typography variant="body1" align="center">
          We couldn't find the registration link for this event. Please try
          again later or contact support.
        </Typography>
      </Box>
    );
  }

  const linkData = data?.getEventRedirectLink;

  if (!linkData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          p: 3,
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Registration Link Not Found
        </Typography>
        <Typography variant="body1" align="center">
          We couldn't find the registration link for this event and role. Please
          check the URL and try again.
        </Typography>
      </Box>
    );
  }

  const { link, utmLink, eventName, eventDescription, eventImage } = linkData;

  // Create redirect URL
  const baseUrl = `https://eventegration.com${link}`;
  const fullUrl = utmLink ? `${baseUrl}?${utmLink}` : baseUrl;

  return (
    <>
      <Helmet>
        <title>{`I'm going to ${eventName}!`}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {/* Open Graph tags for Facebook, LinkedIn etc. */}
        <meta property="og:title" content={`I'm going to ${eventName}!`} />
        <meta
          property="og:description"
          content={eventDescription || "Register Now"}
        />
        <meta property="og:image" content={eventImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`I'm going to ${eventName}!`} />
        <meta
          name="twitter:description"
          content={eventDescription || "Register Now"}
        />
        <meta name="twitter:image" content={eventImage} />

        {/* Redirect script */}
        <script type="text/javascript">
          {`
            function replace() {
              window.location.replace("${fullUrl}");
            }
            window.onload = function() {
              setTimeout(replace, 5000);
            };
          `}
        </script>
      </Helmet>

      <div className="container main-container">
        <div className="row">
          <div className="col-xs-12 text-center">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography variant="h4" gutterBottom>
                I'm going to {eventName}!
              </Typography>

              <Typography variant="body1" gutterBottom>
                Redirecting to registration page in {countdown} seconds...
              </Typography>

              <Typography variant="body2" sx={{ mt: 2 }}>
                If you're not redirected automatically, please click:
                <Link
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "block", mt: 1 }}
                >
                  Register for {eventName}
                </Link>
              </Typography>
            </Box>

            <noscript>
              <h4>
                Taking you to {fullUrl} if you're not redirected, please click:
                <a title={`Go to Registration page`} href={fullUrl}>
                  {fullUrl}
                </a>
              </h4>
            </noscript>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventRedirect;
